/**
 * Centralized Tier Permissions System
 * 
 * This file defines ALL subscription tiers and their permissions.
 * To add a new tier, simply add it to TIER_PERMISSIONS below.
 * 
 * Usage:
 * - Frontend: canUserDo(tier, 'canShare')
 * - Backend: import TIER_PERMISSIONS, check TIER_PERMISSIONS[tier]?.canShare
 */

import { CONSTANTS } from './constants.js';

export const TIER_PERMISSIONS = {
  free: {
    // Display Information
    displayName: 'Free',
    price: 0,
    badge: {
      variant: 'secondary',
      icon: 'bi-gift'
    },
    
    // Numeric Limits
    plansLimit: CONSTANTS.LIMITS.PLANS_LIMIT,              // Max number of trips
    maxDaysPerPlan: CONSTANTS.LIMITS.MAX_DAYS_PER_PLAN,     // Max days per trip
    
    // Feature Permissions (boolean)
    canShare: false,            // Share trips publicly
    canExportPDF: false,        // Export to PDF
    canUseSmartAI: true,        // Use Smart AI generation
    canManuallyAddActivities: true, // Manually add activities
    canUseAiGeneration: true,   // Use AI to generate activities
    canManageGroups: false,     // Manage groups (Pro feature)
    
    // UI Feature List (for pricing page) - uses translation keys
    features: [
      { key: 'pricing.features.free.trips', params: { count: CONSTANTS.LIMITS.PLANS_LIMIT } },
      { key: 'pricing.features.free.maxDays', params: { count: CONSTANTS.LIMITS.MAX_DAYS_PER_PLAN } },
      { key: 'pricing.features.free.smartAI' },
      { key: 'pricing.features.free.manualActivity' },
      { key: 'pricing.features.free.aiSuggestions' },
      { key: 'pricing.features.free.qrTags' }
    ]
  },
  
  pro: {
    // Display Information
    displayName: 'Pro',
    monthlyPrice: CONSTANTS.PRICES.PRO_MONTHLY,
    yearlyPrice: CONSTANTS.PRICES.PRO_YEARLY,
    badge: {
      variant: 'success',
      icon: 'bi-star-fill'
    },
    
    // Numeric Limits (-1 = unlimited)
    plansLimit: -1,
    maxDaysPerPlan: -1,
    groupsLimit: CONSTANTS.LIMITS.GROUPS_LIMIT_PRO,  // Max 5 groups for Pro
    emailsPerGroupLimit: CONSTANTS.LIMITS.EMAILS_PER_GROUP_LIMIT_PRO,  // Max 10 emails per group for Pro
    aiCallsPerDay: CONSTANTS.LIMITS.AI_CALLS_PER_DAY_PRO,
    
    // Feature Permissions (all true for Pro)
    canShare: true,
    canExportPDF: true,
    canUseSmartAI: true,
    canManuallyAddActivities: true,
    canUseAiGeneration: true,
    canManageGroups: true,      // Manage groups
    
    // UI Feature List - uses translation keys
    features: [
      { key: 'pricing.features.pro.unlimitedTrips' },
      { key: 'pricing.features.pro.unlimitedDays' },
      { key: 'pricing.features.pro.smartAI' },
      { key: 'pricing.features.pro.manualActivity' },
      { key: 'pricing.features.pro.aiCallsPerDay', params: { count: CONSTANTS.LIMITS.AI_CALLS_PER_DAY_PRO } },
      { key: 'pricing.features.pro.shareableTrips' },
      { key: 'pricing.features.pro.pdfExport' },
      { key: 'pricing.features.pro.privateGroupsLimited', params: { count: CONSTANTS.LIMITS.GROUPS_LIMIT_PRO } },
      { key: 'pricing.features.pro.qrTags' }
    ]
  },
  
  proplus: {
    // Display Information
    displayName: 'Pro+',
    monthlyPrice: CONSTANTS.PRICES.PRO_PLUS_MONTHLY,
    yearlyPrice: CONSTANTS.PRICES.PRO_PLUS_YEARLY,
    badge: {
      variant: 'warning',
      icon: 'bi-star-fill'
    },
    
    // Numeric Limits (-1 = unlimited)
    plansLimit: -1,
    maxDaysPerPlan: -1,
    groupsLimit: -1,           // Unlimited groups
    aiCallsPerDay: CONSTANTS.LIMITS.AI_CALLS_PER_DAY_PROPLUS,
    
    // Feature Permissions (all true for Pro+)
    canShare: true,
    canExportPDF: true,
    canUseSmartAI: true,
    canManuallyAddActivities: true,
    canUseAiGeneration: true,
    canManageGroups: true,
    hasUnlimitedGroups: true,
    
    // UI Feature List - uses translation keys
    features: [
      { key: 'pricing.features.proplus.allProFeatures' },
      { key: 'pricing.features.proplus.aiCallsPerDay', params: { count: CONSTANTS.LIMITS.AI_CALLS_PER_DAY_PROPLUS } },
      { key: 'pricing.features.proplus.unlimitedGroups' },
      { key: 'pricing.features.proplus.unlimitedMembers' },
      { key: 'pricing.features.proplus.qrTags' },
      { key: 'pricing.features.proplus.idealForOrganizers' }
    ]
  },
  
  admin: {
    // Display Information
    displayName: 'Admin',
    price: 0,
    badge: {
      variant: 'danger',
      icon: 'bi-shield-fill-check'
    },
    
    // Numeric Limits (unlimited for admin)
    plansLimit: -1,
    maxDaysPerPlan: -1,
    
    // Feature Permissions (all true for admin)
    canShare: true,
    canExportPDF: true,
    canUseSmartAI: true,
    canManuallyAddActivities: true,
    canUseAiGeneration: true,
    canAccessDebugSettings: true,
    canViewRawData: true,
    canManageGroups: true,      // Manage groups
    
    // UI Feature List - uses translation keys
    features: [
      { key: 'pricing.features.admin.allProFeatures' },
      { key: 'pricing.features.admin.debugSettings' },
      { key: 'pricing.features.admin.systemConfig' },
      { key: 'pricing.features.admin.rawDataAccess' }
    ]
  }
};

/**
 * Check if a user tier has a specific permission
 * @param {string} tier - The user's subscription tier (free, pro, admin)
 * @param {string} permission - The permission to check (e.g., 'canShare')
 * @returns {boolean} - True if user has permission, false otherwise
 */
export function canUserDo(tier, permission) {
  if (!tier || !permission) return false;
  
  const tierData = TIER_PERMISSIONS[tier];
  if (!tierData) return false;
  
  return tierData[permission] || false;
}

/**
 * Get tier display information
 * @param {string} tier - The subscription tier
 * @returns {Object} - Display info (name, badge variant, icon)
 */
export function getTierDisplay(tier) {
  const tierData = TIER_PERMISSIONS[tier];
  if (!tierData) {
    return {
      displayName: 'Unknown',
      badge: { variant: 'secondary', icon: 'bi-question' }
    };
  }
  
  return {
    displayName: tierData.displayName,
    badge: tierData.badge
  };
}

/**
 * Get tier limits
 * @param {string} tier - The subscription tier
 * @returns {Object} - Limits object
 */
export function getTierLimits(tier) {
  const tierData = TIER_PERMISSIONS[tier];
  if (!tierData) {
    // Return free tier limits as default
    return TIER_PERMISSIONS.free;
  }
  
  return {
    plansLimit: tierData.plansLimit,
    maxDaysPerPlan: tierData.maxDaysPerPlan
  };
}

/**
 * Check if a limit is unlimited
 * @param {number} limit - The limit value (-1 = unlimited)
 * @returns {boolean} - True if unlimited
 */
export function isUnlimited(limit) {
  return limit === -1;
}

